import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import api from '../../../services/api';
import type {
  BIQueryError,
  BIQueryErrorPayload,
  DataConfig,
  DateRange,
  QueryResult,
  ProjectionConfig,
  ConditionalFormatRule,
  WidgetType,
  FilterCondition,
} from '../../../types/bi';
import type { DrilldownFilter } from '../utils/drilldown';
import { buildSemanticIntentFromDataConfig } from '../utils/semanticIntent';

function normalizeQueryError(error: unknown): BIQueryError {
  const payload = (error as { response?: { data?: BIQueryErrorPayload; status?: number } })
    ?.response?.data;

  if (payload?.error) {
    const normalized = new Error(payload.message || payload.error) as BIQueryError;
    normalized.reasonCode = payload.reason_code;
    normalized.details = payload.details;
    normalized.status = (error as { response?: { status?: number } })?.response?.status;
    return normalized;
  }

  const fallback = new Error((error as Error)?.message || 'Query execution failed') as BIQueryError;
  fallback.status = (error as { response?: { status?: number } })?.response?.status;
  return fallback;
}

/**
 * Executes a BI query for a single widget.
 * Automatically re-fetches when data_config, date_range, or drilldown filters change.
 * 5-minute stale time to reduce redundant queries.
 */
export function useWidgetQuery(
  dataConfig: DataConfig | null | undefined,
  widgetType: WidgetType,
  dateRange?: DateRange | null,
  projectionConfig?: ProjectionConfig | null,
  conditionalFormatting?: ConditionalFormatRule[],
  requireDimensions = true,
  requireMeasures = true,
  globalFilters?: FilterCondition[],
  drilldownFilters?: DrilldownFilter[],
  includeExplain = false,
) {
  // Merge dashboard/global and drilldown filters into the widget config.
  const effectiveConfig = useMemo(() => {
    if (!dataConfig) return dataConfig;

    const mergedFilters: FilterCondition[] = [...(dataConfig.filters || [])];

    if (globalFilters?.length) {
      mergedFilters.push(...globalFilters);
    }

    if (drilldownFilters?.length) {
      mergedFilters.push(
        ...drilldownFilters.map((df) => ({
          field_key: df.field_key,
          operator: '=' as const,
          value: df.value,
        })),
      );
    }

    if (mergedFilters.length === (dataConfig.filters || []).length) return dataConfig;

    return {
      ...dataConfig,
      filters: mergedFilters,
    };
  }, [dataConfig, drilldownFilters, globalFilters]);

  const hasConfig =
    !!effectiveConfig &&
    (!requireMeasures ||
      (Array.isArray(effectiveConfig.measures) && effectiveConfig.measures.length > 0)) &&
    (!requireDimensions ||
      (Array.isArray(effectiveConfig.dimensions) && effectiveConfig.dimensions.length > 0));

  const semanticIntent = useMemo(
    () =>
      effectiveConfig
        ? buildSemanticIntentFromDataConfig(effectiveConfig, widgetType, projectionConfig)
        : undefined,
    [effectiveConfig, widgetType, projectionConfig],
  );

  return useQuery<QueryResult>({
    queryKey: [
      'bi',
      'query',
      widgetType,
      effectiveConfig,
      semanticIntent,
      dateRange,
      projectionConfig,
      conditionalFormatting,
      includeExplain,
    ],
    queryFn: () =>
      api
        .post('/api/v1/bi/query/execute', {
          data_config: effectiveConfig,
          semantic_intent: semanticIntent,
          date_range: dateRange ?? undefined,
          projection_config: projectionConfig ?? undefined,
          conditional_formatting: conditionalFormatting?.length ? conditionalFormatting : undefined,
          include_explain: includeExplain || undefined,
        })
        .then((r) => r.data)
        .catch((err) => {
          throw normalizeQueryError(err);
        }),
    enabled: hasConfig,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
